// Feature C — Overlay Depth Pass
// Provides:
//   • drawShadow()       – call BEFORE overlay sprite
//   • drawHoverHalo()    – call for the hovered hex
//   • spawnSparkles()    – call when an overlay is placed
//   • renderSparkles()   – call once per frame

import { hexCorners } from '../../math/hexMath.mjs';
import {
  SHADOW_ALPHA, SHADOW_SCALE_X, SHADOW_SCALE_Y, SHADOW_OFFSET_Y,
  HOVER_HALO_ALPHA, HOVER_HALO_WIDTH,
  SPARKLE_COUNT, SPARKLE_LIFE_MS,
} from './constants.mjs';

// Pool size: allow up to 8 concurrent bursts
const POOL_SIZE = SPARKLE_COUNT * 8;

export class OverlayDepthFx {
  constructor() {
    // Pre-allocated sparkle particle pool (no GC churn)
    this._pool = Array.from({ length: POOL_SIZE }, () => ({
      active: false,
      cx: 0, cy: 0,
      angle: 0,
      radius: 0,
      drift: 0,
      spawnMs: 0,
    }));
  }

  // ── Shadow ─────────────────────────────────────────────────────────────────

  /**
   * Draw a radial-gradient contact shadow under an overlay sprite.
   * Call this in screen-space coordinates BEFORE drawing the overlay.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} cx  – tile screen centre x
   * @param {number} cy  – tile screen centre y
   * @param {number} sz  – hex radius in screen pixels
   */
  drawShadow(ctx, cx, cy, sz) {
    // Scale the offset proportionally to the current hex screen size.
    // Base hex size is 40; the spec says SHADOW_OFFSET_Y = 10 at base size.
    const offsetY = SHADOW_OFFSET_Y * (sz / 40);
    const shadowCx = cx;
    const shadowCy = cy + offsetY;
    const radius   = sz * 0.9;

    ctx.save();
    ctx.translate(shadowCx, shadowCy);
    ctx.scale(SHADOW_SCALE_X, SHADOW_SCALE_Y);

    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
    grad.addColorStop(0, `rgba(0,0,0,${SHADOW_ALPHA})`);
    grad.addColorStop(1, 'rgba(0,0,0,0)');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  // ── Hover halo ─────────────────────────────────────────────────────────────

  /**
   * Draw a faint hex ring around the hovered cell.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} cx
   * @param {number} cy
   * @param {number} sz
   * @param {boolean} hasOverlay  – slightly stronger when an overlay is present
   */
  drawHoverHalo(ctx, cx, cy, sz, hasOverlay) {
    const alpha = hasOverlay ? HOVER_HALO_ALPHA * 1.3 : HOVER_HALO_ALPHA;
    // Draw inside the hex so adjacent tiles don't cover the ring
    const corners = hexCorners(cx, cy, sz * 0.88);

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i].x, corners[i].y);
    ctx.closePath();
    ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
    ctx.lineWidth = HOVER_HALO_WIDTH;
    ctx.stroke();
    ctx.restore();
  }

  // ── Sparkle burst ──────────────────────────────────────────────────────────

  /**
   * Spawn a burst of sparkle particles when an overlay is placed.
   *
   * @param {number} cx     – screen centre x of the placed tile
   * @param {number} cy     – screen centre y of the placed tile
   * @param {number} nowMs  – current timestamp (use performance.now())
   */
  spawnSparkles(cx, cy, nowMs) {
    let spawned = 0;
    for (let i = 0; i < POOL_SIZE && spawned < SPARKLE_COUNT; i++) {
      const p = this._pool[i];
      if (p.active) continue;

      p.active  = true;
      p.cx      = cx;
      p.cy      = cy;
      // Spread evenly around a ring with a small random offset
      p.angle   = (spawned / SPARKLE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.9;
      p.radius  = 6 + Math.random() * 8; // 6–14 px
      p.drift   = 0.4 + Math.random() * 0.6;
      p.spawnMs = nowMs;
      spawned++;
    }
  }

  /**
   * Render all active sparkle particles.
   * Call once per frame after the overlay pass.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} nowMs
   */
  renderSparkles(ctx, nowMs) {
    let anyActive = false;
    for (const p of this._pool) {
      if (!p.active) continue;

      const age = nowMs - p.spawnMs;
      if (age >= SPARKLE_LIFE_MS) {
        p.active = false;
        continue;
      }
      anyActive = true;

      const prog = age / SPARKLE_LIFE_MS; // 0 → 1
      // Drift outward, fade to transparent
      const expand = 1 + prog * p.drift * 14;
      const x      = p.cx + Math.cos(p.angle) * p.radius * expand;
      const y      = p.cy + Math.sin(p.angle) * p.radius * expand;
      const alpha  = (1 - prog) * (1 - prog) * 0.9; // quadratic fade
      const size   = 1.5 + (1 - prog) * 2.5;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#fff7c0';
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    return anyActive;
  }
}
